import { isAnnotationBundleSet } from "@/api/trainingAnnotation";
import type {
  AnnotationMediaResultBinding,
  AnnotationMediaTarget,
  TimedContourSample,
  TrackingPreviewTargetResult,
} from "@carbon6/c6-frontend-design/annotation";
import {
  createAnnotationConnection,
  getAnnotationFrameMeta,
  type AnnotationConnectionResp,
  type AnnotationConnectionReq,
  type AnnotationFrameMetaResp,
  type AnnotationRequestOptions,
  type AnnotationSetSummaryView,
} from "@/api";
import { pixelToNormalized, timestampAtFrameIndex } from "../model/annotationCoordinates";
import { AnnotationIdentityRegistry } from "../model/annotationIdentity";
import { toAnnotationOperationError } from "../model/annotationOperationError";
import { AnnotationTimedContourSource } from "./AnnotationTimedContourSource";
import { HtmlVideoTrackingTransport, type TrackingTransport } from "./HtmlVideoTrackingTransport";
import { TrackingConditioningCache } from "./trackingConditioning";
import {
  createBrowserTrackingSocketFactory,
  createTrackingDriver,
  type TrackingRequest,
  type TrackingSocketFactory,
  type TrackingStreamEvent,
} from "./trackingDriver";
import { FALLBACK_MAX_TRACK_FRAMES } from "./trackingProtocol";

export type AnnotationTrackingStatus =
  | "idle"
  | "starting"
  | "running"
  | "complete"
  | "stopped"
  | "error";

export type AnnotationTrackingSnapshot = Readonly<{
  status: AnnotationTrackingStatus;
  targetMedia: AnnotationMediaTarget | null;
  framesSeen: number;
  error: string | null;
  mediaResult: AnnotationMediaResultBinding | null;
}>;

type IssueConnection = (
  datasetUuid: string,
  payload: AnnotationConnectionReq,
  options: AnnotationRequestOptions,
) => Promise<AnnotationConnectionResp>;

type GetFrameMeta = (
  datasetUuid: string,
  camera: string,
  episode: string,
  options: AnnotationRequestOptions,
) => Promise<AnnotationFrameMetaResp>;

export type OpenTrackingStream = (
  request: TrackingRequest,
  context: {
    initialConnection: AnnotationConnectionResp;
    issueConnection: () => Promise<AnnotationConnectionResp>;
    createSocket: TrackingSocketFactory;
    signal: AbortSignal;
  },
) => AsyncIterable<TrackingStreamEvent>;

const INITIAL_SNAPSHOT: AnnotationTrackingSnapshot = {
  status: "idle",
  targetMedia: null,
  framesSeen: 0,
  error: null,
  mediaResult: null,
};

/** 화면이 지금 보고 있는 미디어. 선택의 두 값이 모두 있어야 좌표가 성립한다. */
export type AnnotationMediaSelection = Readonly<{ sequenceId: string; streamId: string }> | null;

/** 트래킹 액션이 어느 화면에 속하는지. 대상 없음 | 이 화면 | 다른 화면에서 실행 중. */
export type AnnotationTrackingActionScope = "none" | "current" | "elsewhere";

export function annotationMediaSelection(snapshot: {
  selectedSequenceId: string | null;
  selectedStreamId: string | null;
}): AnnotationMediaSelection {
  const { selectedSequenceId, selectedStreamId } = snapshot;
  if (selectedSequenceId === null || selectedStreamId === null) return null;
  return { sequenceId: selectedSequenceId, streamId: selectedStreamId };
}

export function sameAnnotationMedia(
  target: AnnotationMediaTarget | null,
  selection: AnnotationMediaSelection,
): boolean {
  if (target === null || selection === null) return false;
  return target.sequenceId === selection.sequenceId && target.streamId === selection.streamId;
}

/**
 * 이 선택에 표시할 결과. 선택과 맞지 않으면 null 이다. 결과는 스냅샷에 그대로 남으므로, 그
 * 미디어로 돌아오면 같은 바인딩을 다시 전달한다.
 *
 * 제품이 조절할 수 있는 것은 결과를 전달할지 여부 하나뿐이다. c6 는 결과가 붙어 있는 동안
 * 타임라인 컨텍스트와 뷰포트를 결과 쪽에 고정한다(RFC 018 "결과 재생"). 그 상태에서는 캡처
 * 액션을 그리지 않는다.
 */
export function trackingResultFor(
  snapshot: AnnotationTrackingSnapshot,
  selection: AnnotationMediaSelection,
): AnnotationMediaResultBinding | null {
  if (snapshot.mediaResult === null) return null;
  return sameAnnotationMedia(snapshot.targetMedia, selection) ? snapshot.mediaResult : null;
}

/**
 * `elsewhere` 는 결과가 화면에 없다는 뜻이다. 그 자리에서 의미가 있는 것은 진행 중인 실행의
 * 제어뿐이다. 재생과 재시도는 눌러도 보이는 변화가 없다.
 */
export function trackingActionScope(
  snapshot: AnnotationTrackingSnapshot,
  selection: AnnotationMediaSelection,
): AnnotationTrackingActionScope {
  if (snapshot.targetMedia === null) return "none";
  return sameAnnotationMedia(snapshot.targetMedia, selection) ? "current" : "elsewhere";
}

export class AnnotationTrackingController {
  private readonly listeners = new Set<() => void>();
  private snapshot: AnnotationTrackingSnapshot = INITIAL_SNAPSHOT;
  private abort: AbortController | null = null;
  private samples: AnnotationTimedContourSource | null = null;
  private transport: TrackingTransport | null = null;
  private generation = 0;
  private lastTarget: AnnotationMediaTarget | null = null;
  private frameProgressHandle: number | null = null;

  constructor(
    private readonly options: {
      datasetUuid: string;
      identities: AnnotationIdentityRegistry;
      getSelectedSet: () => AnnotationSetSummaryView | null;
      // GPU holder 가 없어 Tracking 상류가 비어 있는지 판정한다. 시작 판정 때마다 읽는다. 생략하면 게이트가 없다.
      getCapacityBlocked?: () => boolean;
      conditioning?: TrackingConditioningCache;
      getFrameMeta?: GetFrameMeta;
      issueConnection?: IssueConnection;
      createSocket?: TrackingSocketFactory;
      openStream?: OpenTrackingStream;
    },
  ) {}

  getSnapshot = (): AnnotationTrackingSnapshot => this.snapshot;

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  get canStart(): boolean {
    const set = this.options.getSelectedSet();
    return (
      set !== null &&
      !isAnnotationBundleSet(set) &&
      set.annotated_target_count > 0 &&
      this.options.getCapacityBlocked?.() !== true &&
      this.snapshot.status !== "starting" &&
      this.snapshot.status !== "running"
    );
  }

  start = async (targetMedia: AnnotationMediaTarget): Promise<void> => {
    if (!this.canStart) return;
    const set = this.options.getSelectedSet();
    const episode = this.options.identities.episode(targetMedia.sequenceId);
    const camera = this.options.identities.camera(targetMedia.streamId);
    if (set === null || episode === null || camera === null) return;

    this.disposeResult();
    const generation = ++this.generation;
    const abort = new AbortController();
    this.abort = abort;
    this.lastTarget = targetMedia;
    this.publish({
      status: "starting",
      targetMedia,
      framesSeen: 0,
      error: null,
      mediaResult: null,
    });

    try {
      const conditioning = this.options.conditioning ?? new TrackingConditioningCache();
      const loaded = await conditioning.load(
        { setUuid: set.uuid, setRevision: set.revision, camera },
        { signal: abort.signal },
      );
      if (abort.signal.aborted || generation !== this.generation) return;

      const connectionPayload: AnnotationConnectionReq = {
        camera,
        episode,
        keyframes: loaded.keyframes.map((keyframe) => ({
          camera: keyframe.camera,
          episode: keyframe.episode,
          frame_index: keyframe.frameIndex,
        })),
      };
      const issueConnection = (): Promise<AnnotationConnectionResp> =>
        (this.options.issueConnection ?? createAnnotationConnection)(
          this.options.datasetUuid,
          connectionPayload,
          { signal: abort.signal },
        );
      const [meta, initialConnection] = await Promise.all([
        (this.options.getFrameMeta ?? getAnnotationFrameMeta)(
          this.options.datasetUuid,
          camera,
          episode,
          { signal: abort.signal },
        ),
        issueConnection(),
      ]);
      if (abort.signal.aborted || generation !== this.generation) return;

      const capacity = meta.num_frames > 0 ? meta.num_frames : FALLBACK_MAX_TRACK_FRAMES;
      const samples = new AnnotationTimedContourSource(capacity);
      const transport = new HtmlVideoTrackingTransport(meta.num_frames / meta.fps);
      this.samples = samples;
      this.transport = transport;
      const mediaResult: AnnotationMediaResultBinding = {
        targetMedia,
        source: {
          id: `annotation-tracking:${set.uuid}:${targetMedia.sequenceId}:${targetMedia.streamId}`,
          revision: generation,
          baseMedia: {
            id: `annotation-tracking-base:${targetMedia.sequenceId}:${targetMedia.streamId}`,
            src: initialConnection.init.video,
            durationSeconds: meta.num_frames / meta.fps,
          },
          layers: [
            {
              id: "annotation-tracking-contours",
              kind: "contour",
              source: samples,
              // hold: 그 시각 이하의 최신 contour 를 유지한다. 프론티어 근처에서 c6 가 스냅샷보다
              // 앞선 시각으로 조회해도(dead reckoning) 오버레이가 사라지지 않는다. tolerance 는
              // 두지 않는다. hold 에서 tolerance 는 빈 화면만 만들 수 있다. "얼마나 앞설 수
              // 있는가" 는 재생 위치를 정하는 transport 가 이미 소유한다.
              timing: { sampling: "hold" },
            },
          ],
        },
        transport,
      };
      this.publish({ ...this.snapshot, status: "running", mediaResult });

      const request: TrackingRequest = {
        camera,
        episode,
        numFrames: meta.num_frames,
        keyframes: loaded.keyframes,
      };
      const stream = (this.options.openStream ?? defaultOpenTrackingStream)(request, {
        initialConnection,
        issueConnection,
        createSocket: this.options.createSocket ?? createBrowserTrackingSocketFactory(),
        signal: abort.signal,
      });
      let complete = false;
      try {
        for await (const event of stream) {
          if (abort.signal.aborted || generation !== this.generation) break;
          if (event.type === "frame") {
            const sample = this.toContourSample(event, meta.fps);
            samples.publish(sample);
            transport.advanceToSeconds(sample.timestampSeconds);
            this.publishFrameProgress({
              ...this.snapshot,
              framesSeen: this.snapshot.framesSeen + 1,
            });
          } else if (event.type === "done") {
            complete = event.reachedEnd;
          }
        }
      } finally {
        // done, 중단, 예외를 한 곳에서 처리한다. 이 실행의 transport 에만 적용한다. 세대가 바뀌었으면
        // this.transport 는 이미 다음 실행의 것이다.
        transport.sealFrontier();
      }
      if (generation !== this.generation) return;
      this.abort = null;
      this.publish({
        ...this.snapshot,
        status: abort.signal.aborted ? "stopped" : complete ? "complete" : "stopped",
      });
    } catch (error) {
      if (generation !== this.generation) return;
      this.abort = null;
      if (abort.signal.aborted) {
        this.publish({ ...this.snapshot, status: "stopped" });
        return;
      }
      const normalized = toAnnotationOperationError("track", error);
      this.publish({
        ...this.snapshot,
        status: "error",
        error: normalized.localizedUserMessage?.ko ?? normalized.userMessage ?? normalized.message,
      });
    }
  };

  stop = (): void => {
    this.abort?.abort();
  };

  retry = (): void => {
    if (this.lastTarget !== null && this.snapshot.status !== "running") {
      void this.start(this.lastTarget);
    }
  };

  replay = (): void => {
    if (this.snapshot.mediaResult === null || this.snapshot.framesSeen === 0) return;
    this.transport?.reset();
    this.transport?.play();
  };

  /**
   * 결과와 결과가 가진 transport·샘플을 해제하고, 다시 시작할 수 있는 초기 상태로 되돌린다.
   * "결과 없음" 상태는 이 메서드만 만든다. 그래서 자원과 스냅샷을 함께 소유한다. 여러 번 호출해도
   * 안전하다.
   *
   * 진행 중이면 스트림을 먼저 끊는다. 그래서 **화면 전환은 이 메서드를 호출하지 않는다.** 다른
   * 카메라를 보는 동안에도 추적은 계속 실행되어야 한다. 끊는 판단은 `stop()` 하나만 한다.
   */
  disposeResult = (): void => {
    // 세대를 늘려야 끊긴 실행의 정리 코드가 이 초기 상태를 "stopped" 로 덮어쓰지 않는다. 결과를
    // 해제한 자리에 소유자 없는 종료 상태가 남으면 다음 판단이 그 상태를 읽는다.
    this.generation += 1;
    this.abort?.abort();
    this.abort = null;
    this.samples?.clear();
    this.samples = null;
    this.transport?.dispose();
    this.transport = null;
    this.publish(INITIAL_SNAPSHOT);
  };

  // 언마운트 cleanup 이 호출한다. StrictMode 재마운트는 같은 인스턴스를 다시 쓴다. 그래서 스트림만
  // 끊고 다시 시작할 수 있는 초기 상태로 되돌린다. 구독의 수명은 조회 수명과 달라서 비우지 않는다.
  dispose = (): void => {
    this.lastTarget = null;
    this.disposeResult();
  };

  private toContourSample(
    event: Extract<TrackingStreamEvent, { type: "frame" }>,
    fps: number,
  ): TimedContourSample {
    const targets: Record<string, TrackingPreviewTargetResult> = {};
    for (const object of event.objects) {
      const targetId = this.options.identities.targetId(object.objId);
      targets[targetId] = {
        targetId,
        contours: object.polygons.map((polygon) =>
          polygon.map((point) => ({
            x: pixelToNormalized(point[0] ?? 0, event.width),
            y: pixelToNormalized(point[1] ?? 0, event.height),
          })),
        ),
      };
    }
    return {
      timestampSeconds: timestampAtFrameIndex(event.frameIndex, fps),
      targets,
    };
  }

  private publish(snapshot: AnnotationTrackingSnapshot): void {
    this.cancelFrameProgress();
    this.snapshot = snapshot;
    this.notify();
  }

  /**
   * 프레임마다 구독자에게 알리면 스튜디오 전체가 초당 수십 번 다시 그려져 디코드가 지연된다.
   * 스냅샷은 즉시 갱신하고, 알림만 애니메이션 프레임당 한 번으로 모은다.
   */
  private publishFrameProgress(snapshot: AnnotationTrackingSnapshot): void {
    this.snapshot = snapshot;
    if (typeof requestAnimationFrame !== "function") {
      this.notify();
      return;
    }
    if (this.frameProgressHandle !== null) return;
    this.frameProgressHandle = requestAnimationFrame(() => {
      this.frameProgressHandle = null;
      this.notify();
    });
  }

  private cancelFrameProgress(): void {
    if (this.frameProgressHandle === null) return;
    if (typeof cancelAnimationFrame === "function") cancelAnimationFrame(this.frameProgressHandle);
    this.frameProgressHandle = null;
  }

  private notify(): void {
    for (const listener of this.listeners) listener();
  }
}

function defaultOpenTrackingStream(
  request: TrackingRequest,
  context: {
    initialConnection: AnnotationConnectionResp;
    issueConnection: () => Promise<AnnotationConnectionResp>;
    createSocket: TrackingSocketFactory;
    signal: AbortSignal;
  },
): AsyncIterable<TrackingStreamEvent> {
  let initial: AnnotationConnectionResp | null = context.initialConnection;
  const driver = createTrackingDriver({
    issueConnection: () => {
      if (initial !== null) {
        const connection = initial;
        initial = null;
        return Promise.resolve(connection);
      }
      return context.issueConnection();
    },
    createSocket: context.createSocket,
  });
  return driver.previewTracking(request, { signal: context.signal });
}
